// react
import { useEffect } from 'react';
// libraries
import { Controller, useForm } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useParams } from '@tanstack/react-router';

// material ui
import {
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';

// api
import { useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units.enhanced';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import {
  RHFAddressAutocomplete,
  RHFAutocomplete,
  RHFSelect,
} from '@/components/ui/form';

// components
import useMatrikelList from '@/features/energyxplorer/portfolio/hooks/useMatrikelList';
import type {
  AnalysisType,
  BuildingWithMatrikelKey,
  PortfolioMeter,
} from '@/features/energyxplorer/types';

// relative
import { createSiteFormSchema, PM_METER_TYPES } from './createSiteForm.schema';
import type {
  CreateSiteFormValues,
  PmmMeterOption,
  PortfolioMeterOption,
  UnifiedMeterOption,
} from './createSiteForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

const PMM_UNIT_TYPE_ID = 30;

interface ReCalcUnit {
  UnitId: number;
  Name?: string;
  UnitType?: { UnitTypeId: number };
  [key: string]: unknown;
}

interface CreateSiteFormProps {
  onSubmit: (values: CreateSiteFormValues) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export default function CreateSiteForm({
  onSubmit,
  onCancel,
  isSubmitting = false,
}: CreateSiteFormProps) {
  const { t } = useTranslation(['forms', 'validation']);
  const { accountId } = useParams({ strict: false });

  const schema = createSiteFormSchema(t);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<CreateSiteFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      siteMode: 'existing',
      existingAddress: null,
      newAddress: null,
      meter: null,
      meterType: '',
    },
    mode: 'onTouched',
  });
  //TODO : Consider using `useWatch` instead of `watch` for performance optimization in large forms.
  // eslint-disable-next-line react-hooks/incompatible-library
  const siteMode = watch('siteMode');
  const selectedMeter = watch('meter');

  // Clear the opposite address field when switching modes
  useEffect(() => {
    if (siteMode === 'existing') {
      setValue('newAddress', null, { shouldValidate: false });
    } else {
      setValue('existingAddress', null, { shouldValidate: false });
    }
  }, [siteMode, setValue]);

  // Clear meterType when a non-PMM meter is selected
  useEffect(() => {
    if (selectedMeter?.source !== 'pmm') {
      setValue('meterType', '', { shouldValidate: false });
    }
  }, [selectedMeter, setValue]);

  // ── Address data ────────────────────────────────────────────────────────────
  const { matrikel_list } = useMatrikelList();

  const existingAddresses: BuildingWithMatrikelKey[] = (() => {
    if (!Array.isArray(matrikel_list)) return [];
    const uniqueBuildings = new Map<string, BuildingWithMatrikelKey>();
    matrikel_list.forEach((item) => {
      if (item.metadata?.MatrikelKey && item.metadata?.Buildings) {
        const matrikelKey = item.metadata.MatrikelKey;
        item.metadata.Buildings.forEach((building) => {
          if (
            building.AdresseBetegnelse &&
            !uniqueBuildings.has(building.AdresseBetegnelse)
          ) {
            uniqueBuildings.set(building.AdresseBetegnelse, {
              ...building,
              matrikelKey,
              City: item.metadata.City,
            });
          }
        });
      }
    });
    return Array.from(uniqueBuildings.values());
  })();

  // ── Meter data ──────────────────────────────────────────────────────────────
  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${accountId})` },
    {
      skip: !accountId,
    },
  ) as { data: ReCalcUnit[] | undefined };
  const pmmUnitIds = new Set<string>(
    (units ?? [])
      .filter((u) => u.UnitType?.UnitTypeId === PMM_UNIT_TYPE_ID)
      .map((u) => String(u.UnitId)),
  );

  const { data: powerMeters } =
    useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
      { analysisType: 'power', accountId },
      { skip: !accountId },
    ) as { data: PortfolioMeter[] | undefined };

  const { data: waterMeters } =
    useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
      { analysisType: 'water', accountId },
      { skip: !accountId },
    ) as { data: PortfolioMeter[] | undefined };

  const { data: heatMeters } =
    useGetPaMetersPortfolioMetersAnalysisTypeAccountIdGetQuery(
      { analysisType: 'heat', accountId },
      { skip: !accountId },
    ) as { data: PortfolioMeter[] | undefined };

  // Unit IDs of PMM meters already linked to a matrikel
  const linkedPmmUnitIds = new Set<string>();
  [powerMeters, waterMeters, heatMeters].forEach((meters) => {
    (meters ?? []).forEach((m) => {
      if (m.MatrikelKey || m.EjerlavKode || m.MatrikelNr) {
        const id = m.ReMoniUnitId || m.UnitId || m.MeteringPointId;
        if (id) linkedPmmUnitIds.add(String(id));
      }
    });
  });

  const isUnassociated = (m: PortfolioMeter) =>
    m.EjerlavKode === null &&
    m.MatrikelNr === null &&
    m.MatrikelKey === null &&
    !pmmUnitIds.has(String(m.UnitId));

  const toPortfolioOption = (
    m: PortfolioMeter,
    analysisType: AnalysisType,
  ): PortfolioMeterOption => ({
    source: 'portfolio',
    meter: m,
    analysisType,
    name: m.DisplayName || m.Name || String(m.UnitId ?? m.MeteringPointId),
  });

  const pmmOptions: PmmMeterOption[] = (units ?? [])
    .filter(
      (u) =>
        u.UnitType?.UnitTypeId === PMM_UNIT_TYPE_ID &&
        !linkedPmmUnitIds.has(String(u.UnitId)),
    )
    .map((u) => ({
      source: 'pmm',
      unitId: u.UnitId,
      name: u.Name ?? String(u.UnitId),
    }));

  const portfolioOptions: PortfolioMeterOption[] = [
    ...(powerMeters ?? [])
      .filter(isUnassociated)
      .map((m) => toPortfolioOption(m, 'power')),
    ...(waterMeters ?? [])
      .filter(isUnassociated)
      .map((m) => toPortfolioOption(m, 'water')),
    ...(heatMeters ?? [])
      .filter(isUnassociated)
      .map((m) => toPortfolioOption(m, 'heat')),
  ];

  const meterOptions: UnifiedMeterOption[] = [
    ...pmmOptions,
    ...portfolioOptions,
  ];

  const getMeterLabel = (option: UnifiedMeterOption) => {
    if (option.source === 'pmm') return `${option.name} (${option.unitId})`;
    const id = option.meter.UnitId ?? option.meter.MeteringPointId;
    return id ? `${option.name} (${id})` : option.name;
  };

  const getMeterGroupLabel = (option: UnifiedMeterOption) =>
    option.source === 'pmm'
      ? t('createSite.meter.groupPmm', { ns: 'forms' })
      : t(`createSite.meter.group.${option.analysisType}`, { ns: 'forms' });

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="subtitle1">
                {t('createSite.title', { ns: 'forms' })}
              </Typography>

              {/* Existing vs new matrikel */}
              <Controller
                name="siteMode"
                control={control}
                render={({ field }) => (
                  <FormControl>
                    <RadioGroup {...field} row>
                      <FormControlLabel
                        value="existing"
                        control={<Radio size="small" />}
                        label={t('createSite.siteMode.existing', {
                          ns: 'forms',
                        })}
                      />
                      <FormControlLabel
                        value="new"
                        control={<Radio size="small" />}
                        label={t('createSite.siteMode.new', { ns: 'forms' })}
                      />
                    </RadioGroup>
                  </FormControl>
                )}
              />

              {siteMode === 'existing' ? (
                <RHFAutocomplete<CreateSiteFormValues, BuildingWithMatrikelKey>
                  name="existingAddress"
                  control={control}
                  options={existingAddresses}
                  getOptionLabel={(option) =>
                    option.AdresseBetegnelse ?? option.matrikelKey
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.matrikelKey === value.matrikelKey
                  }
                  required
                  size="small"
                  label={t('createSite.existingAddress.label', { ns: 'forms' })}
                  placeholder={t('createSite.existingAddress.placeholder', {
                    ns: 'forms',
                  })}
                  helperText={t('createSite.existingAddress.helperText', {
                    ns: 'forms',
                  })}
                  disabled={isSubmitting}
                />
              ) : (
                <RHFAddressAutocomplete
                  name="newAddress"
                  control={control}
                  required
                  fullWidth
                  size="small"
                  label={t('createSite.newAddress.label', { ns: 'forms' })}
                  placeholder={t('createSite.newAddress.placeholder', {
                    ns: 'forms',
                  })}
                  helperText={t('createSite.newAddress.helperText', {
                    ns: 'forms',
                  })}
                  disabled={isSubmitting}
                />
              )}

              {/* Meter selector */}
              <RHFAutocomplete<CreateSiteFormValues, UnifiedMeterOption>
                name="meter"
                control={control}
                options={meterOptions}
                getOptionLabel={getMeterLabel}
                groupBy={getMeterGroupLabel}
                isOptionEqualToValue={(option, value) =>
                  option.source === value.source &&
                  (option.source === 'pmm'
                    ? (option as PmmMeterOption).unitId ===
                      (value as PmmMeterOption).unitId
                    : (option as PortfolioMeterOption).meter.UnitId ===
                      (value as PortfolioMeterOption).meter.UnitId)
                }
                required
                size="small"
                label={t('createSite.meter.label', { ns: 'forms' })}
                placeholder={t('createSite.meter.placeholder', { ns: 'forms' })}
                helperText={t('createSite.meter.helperText', { ns: 'forms' })}
                disabled={isSubmitting}
              />

              {/* PMM meter type — only shown for PMM meters */}
              {selectedMeter?.source === 'pmm' && (
                <RHFSelect
                  name="meterType"
                  control={control}
                  required
                  size="small"
                  label={t('createSite.meterType.label', { ns: 'forms' })}
                  disabled={isSubmitting}
                  options={PM_METER_TYPES.map((type) => ({
                    value: type,
                    label: t(`createSite.meterType.options.${type}`, {
                      ns: 'forms',
                    }),
                  }))}
                  helperText={t('createSite.meterType.helperText', {
                    ns: 'forms',
                  })}
                />
              )}
            </Stack>
          </CardContent>
        </Card>

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <CancelButton onClick={onCancel} disabled={isSubmitting}>
            {t('common.cancel', { ns: 'forms' })}
          </CancelButton>
          <SubmitButton disabled={isSubmitting || !isValid}>
            {isSubmitting
              ? t('createSite.submitting', { ns: 'forms' })
              : t('createSite.submit', { ns: 'forms' })}
          </SubmitButton>
        </Stack>
      </Stack>
    </form>
  );
}
